import React, { useMemo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/core';
import LoginForm from './LoginForm';
import { Str } from '../../localization/translationKeys';
import { loginAction } from './api/loginAction';
import { ActionType } from '../../core/redux/actionType';
import LoginLayout from './LoginLayout';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useError, useLoading } from '../../core/api/hooks';
import Msg from '../../localization/Msg';
import LocalDiv from '../../localization/LocalDiv';
import { parseQueryToKeyedStrings } from '../../navigation/module';
import { useMsgText } from '../../localization/hooks';
import { loginPermission } from './module';
import { useAppState } from '../../core/redux/hooks';
import { PortalType } from '../../navigation/portalType';
import { makeRoutes } from '../../navigation/routes';
import { useMount } from '../../core/hooks/useMount';
import { isSsoAction } from '../sso/IsSsoAction';
import { isNilOrEmpty } from '../../language/module';
import { loginTalentSmartAction } from './api/loginTalentSmartAction';
import { loginBXAction } from './api/loginBXAction';
import { setLocaleAction } from '../../localization/setLocaleAction';
import { TextDirection } from '../../localization/textDirection';
import { Language } from '../../localization/models';
import { authenticateCollaboratorAction } from '../../project/devPlan/collaboration/api/authenticateCollaboratorAction';
import { useCurrentCollaborator } from '../../project/devPlan/collaboration/hooks';
import ExternalRedirect from '../../navigation/ExternalRedirect';
import { Endpoints } from '../../core/api/endpoints';


const styles = {
  loginForm:          css`
    padding-bottom: 30px;
  `,
  forgotPasswordLink: css`
    text-decoration: underline !important;
  `,
  entryAgreement:     css`
    margin-top: 12px;
  `,
};


type Props = {
  className?: string;
  forgotPasswordUrl: string;
  accountSetupUrl: string;
  postLogin: React.ReactNode;
};


type UserQueryObject = {
  passkey?: string;
  email?: string;
  langid?: number;
  auth?: string;
  bx_email?: string;
  bx_uid?: string;
  bx_pid?: string;
  signout_url?: string;
  setup?: string;
};


type CollaboratorQueryObject = {
  projectid?: string;
  participantid?: string;
  collaboratorid?: string;
}


/**
 * LoginPage: a component that renders a page from which a user can enter their username and password to log in and
 * access the dashboard for their particular user type.
 *
 * PureComponent: true
 * Redux Connected: true
 */
const LoginPage: React.FC<Props> = props => {
  const dispatch = useDispatch();

  useMount(() => { dispatch(isSsoAction()); });

  const location = useLocation();

  const portalHeader          = useMsgText(Str.LoginTitle);
  const raterApprovalHeader   = useMsgText(Str.LoginApprovalTitle);
  const reportRecipientHeader = useMsgText(Str.LoginRecipientTitle);
  const announcement          = useMsgText(Str.LoginAnnouncement);

  const raterApprovalLogin   = location.pathname.toLowerCase().indexOf(PortalType.RATER_APPROVAL.toLowerCase()) > -1;
  const reportRecipientLogin = location.pathname.toLowerCase().indexOf(PortalType.REPORT_RECIPIENT.toLowerCase()) > -1;

  const showAnnouncement     = useAppState(state => state.showLoginAnnouncement)

  const header = (
    raterApprovalLogin
      ? raterApprovalHeader
      : reportRecipientLogin
        ? reportRecipientHeader
        : portalHeader
  );

  const allLangs = useAppState(state => state.locale.portalLanguages ?? {});

  const userQueryObj = parseQueryToKeyedStrings<UserQueryObject>(location.search);
  const languageId   = userQueryObj?.langid;
  const setup        = (userQueryObj?.setup ?? '').toLowerCase() === 'true';

  useEffect(
    () => {
      if (!languageId) {
        return;
      }

      const language = Object.values(allLangs).find((lang: Language) => lang.languageId === +languageId);

      if (language && language.languageId > 0) {
        dispatch(setLocaleAction(language, language.rtl ? TextDirection.RTL : TextDirection.LTR));
      }
    },
    [allLangs, dispatch, languageId],
  );

  const login = useCallback(
    (username: string, password: string) => dispatch(loginAction(username, password, raterApprovalLogin)),
    [dispatch, raterApprovalLogin],
  );

  const loginTalentSmart = useCallback(
    (auth: string) => dispatch(loginTalentSmartAction(auth)),
    [dispatch],
  );

  const loginBX = useCallback(
    (email: string, projectId: string, logoutUrl: string) => dispatch(loginBXAction(email, projectId, logoutUrl)),
    [dispatch],
  );

  const loginError  = !!useError(ActionType.LoginRequest);
  const isLoggingIn = useLoading(ActionType.LoginRequest);

  useEffect(
    () => {
      if (loginError) {
        return;
      }

      if (!isNilOrEmpty(userQueryObj.email ?? '') && !isNilOrEmpty(userQueryObj.passkey ?? '')) {
        login(userQueryObj.email ?? '', userQueryObj.passkey ?? '');
      }

      if (!isNilOrEmpty(userQueryObj.auth ?? '')) {
        loginTalentSmart(userQueryObj.auth ?? '');
      }

      const em = userQueryObj.bx_email ?? '';

      if (!isNilOrEmpty(em)) {
        const signout = userQueryObj.signout_url ?? '';
        const uId     = userQueryObj.bx_uid ?? '';
        const pId     = userQueryObj.bx_pid ?? '';
        if (!raterApprovalLogin && !reportRecipientLogin && !isNilOrEmpty(uId)) {
          loginBX(em, '', signout);
        }
        if (reportRecipientLogin && !isNilOrEmpty(pId)) {
          loginBX(em, pId, signout);
        }
      }
    },
    [
      login,
      loginBX,
      loginError,
      loginTalentSmart,
      userQueryObj.auth,
      userQueryObj.bx_email,
      userQueryObj.bx_pid,
      userQueryObj.bx_uid,
      userQueryObj.email,
      userQueryObj.passkey,
      userQueryObj.signout_url,
      raterApprovalLogin,
      reportRecipientLogin,
    ],
  );

  const { projectid, participantid, collaboratorid } = parseQueryToKeyedStrings<CollaboratorQueryObject>(
    location.search,
  );

  useEffect(
    () => {
      if (!!projectid && !!participantid && !!collaboratorid) {
        dispatch(authenticateCollaboratorAction(
          projectid ?? '',
          +(participantid ?? '-1'),
          collaboratorid ?? '',
          ),
        );
      }
    },
    [collaboratorid, dispatch, participantid, projectid],
  );

  const collaboratorInfo = useCurrentCollaborator();

  const userInfo = useAppState(state => state.auth.currentUser);
  const ssoInfo  = useAppState(state => state.auth.ssoRedirect);

  const isAuthorized = useMemo(
    () => !!userInfo && !userInfo.locked && loginPermission(location.pathname, userInfo),
    [location.pathname, userInfo],
  );

  const notAuthorized = useMemo(
    () => !!userInfo && !loginPermission(location.pathname, userInfo),
    [location.pathname, userInfo],
  );

  const errorMsg = useMemo(
    () => loginError
      ? Str.LoginFailure
      : notAuthorized
        ? Str.PermissionMsg
        : !!userInfo?.locked
          ? Str.AccountLocked
          : '',
    [loginError, notAuthorized, userInfo],
  );

  return (
    <LoginLayout 
      isLoading={isLoggingIn} 
      className={props.className} 
      headerMsg={header} 
      showAnnouncement={showAnnouncement}
      announcementMsg={announcement}
    >
      <LoginForm
        css={styles.loginForm}
        onLoginAttempt={login}
        errorMsg={errorMsg}
        isLoggingIn={isLoggingIn}
        initialEmail={userQueryObj?.email}
        initialPassword={userQueryObj?.passkey}
      />
      {
        !!ssoInfo && ssoInfo.isSso && !userInfo && (
          <ExternalRedirect
            pathname={Endpoints.SamlLogin}
            search={'?relay=' + (
              reportRecipientLogin
              ? PortalType.REPORT_RECIPIENT.toLowerCase() 
              : raterApprovalLogin 
                ? PortalType.RATER_APPROVAL.toLowerCase() 
                : '/'
            )}
          />
        )
      }
      {
        !!collaboratorInfo && (
          <Redirect to={makeRoutes(participantid, projectid).CollaboratorDevPlan}/>
        )
      }
      {
        isAuthorized && (
          userInfo!.requirePasswordChange || (setup && userInfo!.securityQuestionId === 0)
          ? <Redirect to={props.accountSetupUrl} />
          : props.postLogin
        )
      }
      <Link to={props.forgotPasswordUrl} css={styles.forgotPasswordLink}>
        <Msg str={Str.ForgotPassword} />
      </Link>
      <LocalDiv css={styles.entryAgreement}>
        <Msg str={Str.EntryAgreement} />
      </LocalDiv>
    </LoginLayout>
  );
};


export default LoginPage;
